import { useEffect, useRef, useState } from "react";
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { InvestigationsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import {
  InvestigationActivityCase,
  InvestigationActivityPage,
  InvestigationEvent,
  Investigation,
  InvestigationAssigneesResult,
  InvestigationCommentRequest,
  InvestigationConflict,
  InvestigationCreateRequest,
  InvestigationCreateResult,
  InvestigationInbox,
  InvestigationInboxReadRequest,
  InvestigationInboxReadResult,
  InvestigationDeletionResponse,
  InvestigationDetail,
  InvestigationExtendResponse,
  InvestigationSplitListResult,
  InvestigationWidenRuleResponse,
  InvestigationReleaseReturnedResponse,
  InvestigationItemsResult,
  InvestigationListResult,
  InvestigationReturnedItemsPage,
  InvestigationMergeRequest,
  InvestigationMergeResult,
  InvestigationPreview,
  InvestigationRedriveList,
  InvestigationRowRequest,
  InvestigationRowResolution,
  InvestigationPredicate,
  InvestigationSuggestionsResult,
  InvestigationTrackStarsRunPatterns,
  InvestigationTransferRequest,
  InvestigationAttemptKind,
  InvestigationExternalRedriveExport,
  InvestigationRedriveAttempt,
  InvestigationUpdateRequest,
  Stage,
  UninvestigatedGroupsResult,
  TrackStarsReportImportResult,
} from "../lusca-search-api/generated-src";
import {
  GroupsFilterQuery,
  parseGroupsFilter,
  serializeGroupsFilter,
} from "../components/investigations/groupsFilter";
import { shownStatus } from "../components/investigations/investigationUtils";

// The backlog filter helpers are pure and live outside this module so tests that automock the
// hooks keep them working; they are re-exported here for callers that reach them via the hooks.
export type { GroupsFilterQuery };
export { parseGroupsFilter, serializeGroupsFilter };

type InvestigationRule = Array<InvestigationPredicate>;

export const investigationKeys = {
  list: (stage: Stage, params: Record<string, string | undefined>) =>
    ["investigations", "list", stage, params] as const,
  detail: (stage: Stage, id: string) =>
    ["investigations", "detail", stage, id] as const,
  redrives: (stage: Stage, id: string) =>
    ["investigations", "redrives", stage, id] as const,
  returnedItems: (stage: Stage, id: string) =>
    ["investigations", "returned-items", stage, id] as const,
  preview: (stage: Stage, id: string | null) =>
    ["investigations", "preview", stage, id] as const,
  uninvestigated: (stage: Stage, uncategorizedOnly: boolean) =>
    ["investigations", "uninvestigated", stage, uncategorizedOnly] as const,
  row: (stage: Stage, dlqPk: string) =>
    ["investigations", "row", stage, dlqPk] as const,
  assignees: (stage: Stage) => ["investigations", "assignees", stage] as const,
  suggestions: (stage: Stage) =>
    ["investigations", "suggestions", stage] as const,
  /** Keyed by run id: a new run is a new report, the old one never changes. */
  runPatterns: (stage: Stage, id: string, runId: string) =>
    ["investigations", "runPatterns", stage, id, runId] as const,
  /** Cross-stage: one inbox per signed-in user. */
  inbox: () => ["investigations", "inbox"] as const,
};

/** Normalised API failure surfaced to the UI (409 conflicts carry the current investigation). */
export interface InvestigationApiError {
  status: number;
  message: string;
  code?: InvestigationConflict["code"];
  current?: Investigation;
  conflicts?: InvestigationConflict["conflicts"];
  /** REFRESH_IN_PROGRESS carries the preview that is already running. */
  preview?: InvestigationPreview;
  /** DELETION_PENDING carries the request that locks the population. */
  pendingDeletion?: InvestigationConflict["pendingDeletion"];
  /** MANIFEST_DRIFTED lists (up to 50) manifest PKs the investigation no longer owns. */
  driftedCount?: number;
  driftedDlqPks?: string[];
}

const isSettledPreview = (preview?: InvestigationPreview) =>
  preview?.status === "COMPLETED" || preview?.status === "FAILED";

const isApiError = (error: unknown): error is InvestigationApiError =>
  !!error &&
  typeof error === "object" &&
  typeof (error as InvestigationApiError).status === "number" &&
  typeof (error as InvestigationApiError).message === "string" &&
  !axios.isAxiosError(error);

/**
 * How often a running preview is polled. Each poll is also what advances the preview server-side
 * (RUNNING → PROCESSING hands the result to the worker), so the interval is paid twice per preview;
 * a cached Athena result finishes in well under a second.
 */
export const PREVIEW_POLL_INTERVAL_MS = 750;

export const previewPollInterval = (
  preview?: InvestigationPreview
): number | false =>
  isSettledPreview(preview) ? false : PREVIEW_POLL_INTERVAL_MS;

export function toApiError(error: unknown): InvestigationApiError {
  if (isApiError(error)) return error;
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<
      InvestigationConflict & { error?: string }
    >;
    const data = axiosError.response?.data;
    return {
      status: axiosError.response?.status || 0,
      message: data?.error || axiosError.message || "Request failed",
      code: data?.code,
      current: data?.current,
      conflicts: data?.conflicts,
      preview: data?.preview,
      pendingDeletion: data?.pendingDeletion,
      driftedCount: data?.driftedCount,
      driftedDlqPks: data?.driftedDlqPks,
    };
  }
  return {
    status: 0,
    message: error instanceof Error ? error.message : String(error),
  };
}

/**
 * Run an API call so that a failure rejects with an `InvestigationApiError` rather than a raw
 * AxiosError. Every investigation mutation goes through this: the components branch on the
 * server's conflict `code` (PREVIEW_STALE, VERSION_CONFLICT, DUPLICATE_RULE, …) and read
 * `current`, which only exist once the 409 body has been lifted out of the axios response.
 */
async function apiCall<T>(call: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await call()).data;
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal -- a plain shape by design: components read `.code` / `.current`
    throw toApiError(error);
  }
}

export interface ListFilters {
  view?: string;
  status?: string;
  owner?: string;
  unclaimed?: boolean;
  redriven?: boolean;
  returned?: boolean;
}

const LIST_PAGE_SIZE = 50;

export function useInvestigationList(
  stage: Stage,
  filters: ListFilters,
  enabled = true
) {
  const api = InvestigationsApiFactory();
  const queryClient = useQueryClient();
  const key = investigationKeys.list(stage, {
    view: filters.view,
    status: filters.status,
    owner: filters.owner,
    unclaimed: filters.unclaimed ? "true" : undefined,
    redriven: filters.redriven ? "true" : undefined,
    returned: filters.returned ? "true" : undefined,
  });
  // One cache entry holds every loaded page, so the 30s refetch walks all of them with fresh
  // cursors (later pages never go stale next to a refreshed page 1) and every page shares the
  // same key across components.
  const query = useInfiniteQuery<
    InvestigationListResult,
    InvestigationApiError
  >(
    key,
    ({ pageParam }) =>
      apiCall(() =>
        api.listInvestigations(
          stage,
          filters.view as never,
          filters.status as never,
          filters.owner,
          filters.unclaimed,
          filters.redriven,
          filters.returned,
          LIST_PAGE_SIZE,
          pageParam as string | undefined
        )
      ),
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: "always",
      refetchInterval: 30000,
      getNextPageParam: (last) => last.nextToken || undefined,
    }
  );
  const pages = query.data?.pages ?? [];
  // A case can move between pages while they load; show it once.
  const seen = new Set<string>();
  const items: Investigation[] = pages
    .flatMap((p) => p.items || [])
    .filter((inv) => !seen.has(inv.id) && !!seen.add(inv.id))
    // Merged sources and auto-closed duplicates are RESOLVED on the wire but count as cancelled:
    // never list or count them as resolved (the backend filters them too; this covers older
    // deploys and keeps every consumer — tables, the Resolved tile — agreeing).
    .filter(
      (inv) => filters.view !== "resolved" || shownStatus(inv) === "RESOLVED"
    );
  const loadMore = async () => {
    if (!query.hasNextPage || query.isFetchingNextPage) return;
    await query.fetchNextPage();
  };
  /** Drop auto-loaded pages and refetch the first one (after a mutation changed membership). */
  const reset = () => {
    queryClient.setQueryData<InfiniteData<InvestigationListResult>>(
      key,
      (old) =>
        old && {
          pages: old.pages.slice(0, 1),
          pageParams: old.pageParams.slice(0, 1),
        }
    );
    return query.refetch();
  };
  return {
    ...query,
    /** The first page: carries the registry / snapshot timestamps. */
    data: pages[0],
    items,
    hasMore: !!query.hasNextPage,
    loadMore,
    reset,
  };
}

export type GroupsSortField =
  | "impactUsd"
  | "count"
  | "ambiguousCount"
  | "eventsourcekey"
  | "errorType"
  | "errorMessage"
  | "errorCategory"
  | "latestDlqTimestamp"
  | "growth7d";

export interface GroupsQuery {
  /** 0-based group offset into the (filtered, sorted) reconciled set. */
  offset: number;
  limit: number;
  /** Server-side substring filter (queue, error type, message, category). */
  q?: string;
  /**
   * Structured server-side filter: a Cloudscape PropertyFilter query ({operation, tokens}),
   * JSON-encoded on the wire. Omitted when there are no tokens.
   */
  filter?: GroupsFilterQuery;
  /** Defaults to highest impact first. */
  sort?: GroupsSortField;
  desc?: boolean;
  /**
   * Slices to count over the whole reconciliation (not just this query's filter), in order; the
   * response's `filterCounts` answers position for position. For navigation badges.
   */
  countFilters?: GroupsFilterQuery[];
  /**
   * The category view: categorized groups collapse into one row per error category (with a
   * (queue, error type) `members` split); uncategorized groups stay one row each.
   */
  byCategory?: boolean;
}

/** Default page: the top groups by impact, enough for the work-queue preview and funnel totals. */
export const DEFAULT_GROUPS_QUERY: GroupsQuery = { offset: 0, limit: 25 };

/**
 * One page of the reconciled "not yet investigated" groups, filtered and sorted server-side —
 * the same shape as useInvestigationItems. Gamma has thousands of groups, so the browser only
 * ever holds the current page; keepPreviousData avoids an empty flash while the next page loads.
 * Every response also carries the stage-wide totals (items, impact, group count) and the distinct
 * queue / error type / category values for filter suggestions.
 */
export function useUninvestigatedGroups(
  stage: Stage,
  uncategorizedOnly: boolean,
  query: GroupsQuery = DEFAULT_GROUPS_QUERY,
  enabled = true
) {
  const api = InvestigationsApiFactory();
  const filter = serializeGroupsFilter(query.filter);
  const countFilters = query.countFilters?.length
    ? JSON.stringify(query.countFilters)
    : undefined;
  return useQuery<UninvestigatedGroupsResult, InvestigationApiError>(
    [
      ...investigationKeys.uninvestigated(stage, uncategorizedOnly),
      query.offset,
      query.limit,
      query.q || "",
      filter || "",
      query.sort || "impactUsd",
      query.desc ?? true,
      countFilters || "",
      query.byCategory ?? false,
    ] as const,
    async () =>
      apiCall(() =>
        api.listUninvestigatedGroups(
          stage,
          uncategorizedOnly,
          query.byCategory || undefined,
          query.limit,
          query.offset,
          query.q || undefined,
          filter,
          countFilters,
          query.sort || "impactUsd",
          query.desc ?? true
        )
      ),
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: "always",
      keepPreviousData: true,
    }
  );
}

/**
 * Investigation suggestions from the latest imported TrackStars DLQ analysis report: each pattern
 * as a derived population rule with its root cause, evidence and whether an active case already
 * covers it. `available=false` (no report for the stage) is a normal, quiet outcome.
 */
export function useInvestigationSuggestions(stage: Stage, enabled = true) {
  const api = InvestigationsApiFactory();
  return useQuery<InvestigationSuggestionsResult, InvestigationApiError>(
    investigationKeys.suggestions(stage),
    () => apiCall(() => api.listInvestigationSuggestions(stage)),
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: "always",
      staleTime: 60 * 1000,
    }
  );
}

/**
 * The patterns of the investigation's own completed TrackStars run, shaped as suggestion cards.
 * Only fetched once the run is COMPLETED (runId given); a run's report never changes afterwards.
 */
export function useInvestigationTrackStarsRunPatterns(
  stage: Stage,
  investigationId: string,
  runId: string | undefined
) {
  const api = InvestigationsApiFactory();
  return useQuery<InvestigationTrackStarsRunPatterns, InvestigationApiError>(
    investigationKeys.runPatterns(stage, investigationId, runId ?? ""),
    () =>
      apiCall(() =>
        api.getInvestigationTrackStarsRunPatterns(stage, investigationId)
      ),
    { enabled: !!runId, retry: false, staleTime: Infinity }
  );
}

/**
 * Upload a TrackStars report as the stage's latest. Suggestions and the backlog's TrackStars marks
 * are both derived from it, so both are refetched.
 */
export function useImportTrackStarsReport(stage: Stage) {
  const api = InvestigationsApiFactory();
  const queryClient = useQueryClient();
  return useMutation<
    TrackStarsReportImportResult,
    InvestigationApiError,
    Record<string, unknown>
  >(
    async (report) =>
      apiCall(() => api.importTrackStarsReport(stage, { report })),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries(investigationKeys.suggestions(stage))
          .catch(console.error);
        queryClient
          .invalidateQueries(["investigations", "uninvestigated", stage])
          .catch(console.error);
      },
    }
  );
}

/**
 * Aliases allowed to own an investigation: everyone who can unlock the Lusca Search Bindle lock.
 * Only fetched while a picker is open (enabled); cached for the session since the list moves slowly.
 */
export function useInvestigationAssignees(stage: Stage, enabled = true) {
  const api = InvestigationsApiFactory();
  return useQuery<InvestigationAssigneesResult, InvestigationApiError>(
    investigationKeys.assignees(stage),
    () => apiCall(() => api.listInvestigationAssignees(stage)),
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: true,
      staleTime: 10 * 60 * 1000,
    }
  );
}

export const INVESTIGATION_POLL_INTERVAL_MS = 15000;
export const CODE_REVIEW_CHECK_POLL_INTERVAL_MS = 3000;
export const CODE_REVIEW_CHECK_FAST_POLL_WINDOW_MS = 2 * 60 * 1000;

/**
 * A just-attached CR is read on the worker within seconds, so the detail page polls fast while it
 * is CHECKING, but only for the first couple of minutes: a check can stay CHECKING for up to an
 * hour, and a failed lookup is retried hourly.
 */
export function investigationPollInterval(
  data?: InvestigationDetail,
  now: number = Date.now()
): number {
  const investigation = data?.investigation;
  const codeReview = investigation?.blocker?.codeReview;
  if (
    investigation?.status !== "BLOCKED" ||
    codeReview?.state !== "CHECKING" ||
    codeReview.lastError
  )
    return INVESTIGATION_POLL_INTERVAL_MS;
  const addedAt = Date.parse(codeReview.addedAt || "");
  return Number.isFinite(addedAt) &&
    now - addedAt < CODE_REVIEW_CHECK_FAST_POLL_WINDOW_MS
    ? CODE_REVIEW_CHECK_POLL_INTERVAL_MS
    : INVESTIGATION_POLL_INTERVAL_MS;
}

export function useInvestigation(stage: Stage, investigationId: string | null) {
  const api = InvestigationsApiFactory();
  return useQuery<InvestigationDetail, InvestigationApiError>(
    investigationKeys.detail(stage, investigationId || ""),
    () => apiCall(() => api.getInvestigation(stage, investigationId!)),
    {
      enabled: !!investigationId,
      retry: false,
      refetchOnWindowFocus: "always",
      refetchInterval: (data) => investigationPollInterval(data),
    }
  );
}

export const RETURNED_ITEMS_PAGE_SIZE = 20;

/**
 * Every redriven message that came back to the DLQ (can be 100k+), newest first. Cursor pages
 * accumulate in one cache entry; the table asks for the next page when the user pages past the
 * last loaded one. Refetched when the investigation's returnedCount changes (key includes it).
 */
export function useInvestigationReturnedItems(
  stage: Stage,
  investigationId: string | null,
  returnedCount: number
) {
  const api = InvestigationsApiFactory();
  return useInfiniteQuery<
    InvestigationReturnedItemsPage,
    InvestigationApiError
  >(
    [
      ...investigationKeys.returnedItems(stage, investigationId || ""),
      returnedCount,
    ],
    ({ pageParam }) =>
      apiCall(() =>
        api.listInvestigationReturnedItems(
          stage,
          investigationId!,
          RETURNED_ITEMS_PAGE_SIZE,
          pageParam as string | undefined
        )
      ),
    {
      enabled: !!investigationId && returnedCount > 0,
      retry: false,
      keepPreviousData: true,
      getNextPageParam: (last) => last.nextToken || undefined,
    }
  );
}

export function useInvestigationRedrives(
  stage: Stage,
  investigationId: string | null,
  enabled = true
) {
  const api = InvestigationsApiFactory();
  const queryClient = useQueryClient();
  return useQuery<InvestigationRedriveList, InvestigationApiError>(
    investigationKeys.redrives(stage, investigationId || ""),
    () => apiCall(() => api.listInvestigationRedrives(stage, investigationId!)),
    {
      enabled: !!investigationId && enabled,
      retry: false,
      refetchOnWindowFocus: "always",
      refetchInterval: (data) =>
        data?.attempts?.some(
          (a) => a.status === "STARTED" || a.status === "RUNNING"
        )
          ? 5000
          : false,
      // Listing attempts is what advances a finished run server-side
      // (EXECUTING_REDRIVE → MONITORING_REDRIVE). The detail query is on its own 15s timer,
      // so without this the header keeps saying "Executing redrive" beside a Completed row.
      onSuccess: (data) => {
        if (!investigationId) return;
        const detailKey = investigationKeys.detail(stage, investigationId);
        const detail = queryClient.getQueryData<InvestigationDetail>(detailKey);
        const running = data.attempts?.some(
          (a) => a.status === "STARTED" || a.status === "RUNNING"
        );
        if (detail?.investigation?.status === "EXECUTING_REDRIVE" && !running) {
          queryClient.invalidateQueries(detailKey).catch(() => undefined);
        }
      },
    }
  );
}

/**
 * The one query behind `investigationKeys.preview`: the lookup, the rule builder and the
 * population-refresh tracker all poll the same preview, so they must share the fetcher (and its
 * InvestigationApiError shape) — whichever mounts first fills the cache for the others.
 */
function usePreviewQuery(stage: Stage, previewId: string | null) {
  const api = InvestigationsApiFactory();
  return useQuery<InvestigationPreview, InvestigationApiError>(
    investigationKeys.preview(stage, previewId),
    () => apiCall(() => api.getInvestigationPreview(stage, previewId!)),
    {
      enabled: !!previewId,
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: previewPollInterval,
    }
  );
}

export interface DlqLookupRequest {
  dlqPk?: string;
  transactionId?: string;
}

/**
 * Fetch DLQ items' snapshot fields by DLQ PK or transaction/order ID (async Athena lookup via the
 * preview machinery). A PK matches at most one item; a transaction may match several.
 */
export function useDlqItemLookup(
  stage: Stage,
  request: DlqLookupRequest | null
) {
  const api = InvestigationsApiFactory();
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [startError, setStartError] = useState<InvestigationApiError | null>(
    null
  );
  const requestKey = request
    ? `${request.dlqPk ?? ""}|${request.transactionId ?? ""}`
    : "";

  useEffect(() => {
    setPreviewId(null);
    setStartError(null);
    if (!request?.dlqPk && !request?.transactionId) return undefined;
    let cancelled = false;
    apiCall(() =>
      api.startInvestigationPreview(stage, {
        dlqPk: request.dlqPk,
        transactionId: request.transactionId,
      })
    )
      .then((data) => {
        if (!cancelled) setPreviewId(data.previewId);
      })
      .catch((err: InvestigationApiError) => {
        if (!cancelled) setStartError(err);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restart only when the lookup changes
  }, [stage, requestKey]);

  const query = usePreviewQuery(stage, previewId);
  const preview = previewId ? query.data : undefined;
  const samples = preview?.status === "COMPLETED" ? preview.samples || [] : [];
  return {
    samples,
    count: preview?.status === "COMPLETED" ? preview.count ?? 0 : undefined,
    sample: samples[0],
    notFound: preview?.status === "COMPLETED" && (preview.count ?? 0) === 0,
    pending:
      !!requestKey &&
      !startError &&
      (!preview ||
        preview.status === "RUNNING" ||
        preview.status === "PROCESSING"),
    error:
      startError ||
      (preview?.status === "FAILED"
        ? { status: 0, message: preview.error || "Lookup failed" }
        : null),
  };
}

export type ItemsSortField =
  | "dlq_pk"
  | "dlq_timestamp"
  | "transaction_date"
  | "eventsourcekey"
  | "error_type"
  | "error_message"
  | "usecase"
  | "transaction_id";

export interface ItemsQuery {
  /** 0-based row offset into the (filtered, sorted) manifest. */
  offset: number;
  limit: number;
  /** Server-side substring filter (PK, queue, error, use case, transaction/order ID). */
  q?: string;
  /**
   * Structured PropertyFilter query over manifest columns (same wire shape as the backlog filter);
   * a clicked breakdown value becomes a `field = value` token here.
   */
  filter?: GroupsFilterQuery;
  sort?: ItemsSortField;
  desc?: boolean;
  /**
   * "live" (server default): the items the case owns that were still in the DLQ at the last
   * reconcile. "all": every item the case was frozen with, each marked `inDlq`.
   */
  scope?: ItemsScope;
}

export type ItemsScope = "live" | "all";

/** Page size the Items tab opens with; the prefetch below must request the same page. */
export const DEFAULT_ITEMS_PAGE_SIZE = 50;

/** The first page the Items tab shows before the user touches sort, filter or paging. */
const DEFAULT_ITEMS_QUERY: ItemsQuery = {
  offset: 0,
  limit: DEFAULT_ITEMS_PAGE_SIZE,
  sort: "dlq_timestamp",
  desc: true,
};

/**
 * The manifest is frozen (the live list changes at most hourly), so a page stays valid for a while: this lets a prefetched or previously
 * viewed page render from cache without a refetch spinner when the Items tab is (re)opened.
 */
const ITEMS_STALE_MS = 2 * 60 * 1000;

const itemsQueryKey = (
  stage: Stage,
  investigationId: string | null,
  query: ItemsQuery
) =>
  [
    "investigations",
    "items",
    stage,
    investigationId,
    query.offset,
    query.limit,
    query.q || "",
    serializeGroupsFilter(query.filter) || "",
    query.sort || "dlq_timestamp",
    query.desc ?? true,
    query.scope || "live",
  ] as const;

const fetchInvestigationItems = async (
  api: ReturnType<typeof InvestigationsApiFactory>,
  stage: Stage,
  investigationId: string,
  query: ItemsQuery
): Promise<InvestigationItemsResult> =>
  apiCall(() =>
    api.listInvestigationItems(
      stage,
      investigationId,
      query.limit,
      query.offset,
      query.q || undefined,
      serializeGroupsFilter(query.filter),
      query.sort || "dlq_timestamp",
      query.desc ?? true,
      query.scope
    )
  );

/**
 * One page of the frozen manifest, filtered and sorted server-side. A manifest can hold 100k
 * rows, so the browser only ever holds the current page; keepPreviousData avoids a flash of
 * empty table while the next page loads.
 */
export function useInvestigationItems(
  stage: Stage,
  investigationId: string | null,
  query: ItemsQuery = DEFAULT_ITEMS_QUERY
) {
  const api = InvestigationsApiFactory();
  return useQuery<InvestigationItemsResult, InvestigationApiError>(
    itemsQueryKey(stage, investigationId, query),
    () => fetchInvestigationItems(api, stage, investigationId!, query),
    {
      enabled: !!investigationId,
      retry: false,
      // The manifest is frozen, so a tab switch / Cmd+F focus change only refetches once stale.
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      staleTime: ITEMS_STALE_MS,
    }
  );
}

/**
 * Warm the Items tab: the tab content only mounts when selected, so without this the first page
 * of the manifest (plus its breakdown and histogram) is requested when the user clicks the tab.
 * Fetching it as soon as the page knows the investigation ID means the tab usually opens from
 * cache. Uses the exact key `useInvestigationItems` builds for its default query.
 */
export function usePrefetchInvestigationItems(
  stage: Stage,
  investigationId: string | null
) {
  const queryClient = useQueryClient();
  const api = InvestigationsApiFactory();
  useEffect(() => {
    if (!investigationId) return;
    queryClient
      .prefetchQuery(
        itemsQueryKey(stage, investigationId, DEFAULT_ITEMS_QUERY),
        () =>
          fetchInvestigationItems(
            api,
            stage,
            investigationId,
            DEFAULT_ITEMS_QUERY
          ),
        { staleTime: ITEMS_STALE_MS, retry: false }
      )
      // Best effort: the tab shows its own error state if the real query fails later.
      .catch(() => undefined);
    // `api` is a fresh factory instance every render; the request depends only on stage + id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, stage, investigationId]);
}

export function useResolveRow(
  stage: Stage,
  request: InvestigationRowRequest | null
) {
  const api = InvestigationsApiFactory();
  return useQuery<InvestigationRowResolution, InvestigationApiError>(
    investigationKeys.row(stage, request?.dlqPk || ""),
    () => apiCall(() => api.resolveInvestigationRow(stage, request!)),
    {
      enabled: !!request?.dlqPk,
      retry: false,
      refetchOnWindowFocus: false,
      cacheTime: 0,
    }
  );
}

/**
 * Recently started previews keyed by (stage, serialised rule). Re-opening the same row, or the
 * builder re-running the rule the resolver already previewed, reuses the preview instead of
 * starting another Athena query. Short-lived: the backend rejects a preview whose snapshot moved
 * on (PREVIEW_STALE), and callers evict on that or on FAILED.
 */
const RECENT_PREVIEW_TTL_MS = 5 * 60 * 1000;
const recentPreviews = new Map<string, { previewId: string; at: number }>();
const recentPreviewKey = (stage: Stage, signature: string) =>
  `${stage}|${signature}`;

export function rememberPreview(
  stage: Stage,
  rule: InvestigationRule,
  previewId: string
) {
  recentPreviews.set(recentPreviewKey(stage, JSON.stringify(rule)), {
    previewId,
    at: Date.now(),
  });
}

export function forgetPreview(stage: Stage, rule: InvestigationRule) {
  recentPreviews.delete(recentPreviewKey(stage, JSON.stringify(rule)));
}

/**
 * Drop the cached preview a case was just created from (or that a create attempt was refused
 * for). Its overlap counts were computed before the claim, so a second click on the same rule
 * would otherwise be offered "Create" for a population that is now owned; re-previewing shows
 * the new case instead. Cheap: the map is per tab and short-lived.
 *
 * @internal Exported for tests only; callers go through useCreateInvestigation.
 */
export function forgetPreviewById(previewId: string) {
  Array.from(recentPreviews.entries())
    .filter(([, entry]) => entry.previewId === previewId)
    .forEach(([key]) => recentPreviews.delete(key));
}

export function recentPreviewId(
  stage: Stage,
  rule: InvestigationRule,
  now = Date.now()
): string | null {
  const key = recentPreviewKey(stage, JSON.stringify(rule));
  const entry = recentPreviews.get(key);
  if (!entry) return null;
  if (now - entry.at > RECENT_PREVIEW_TTL_MS) {
    recentPreviews.delete(key);
    return null;
  }
  return entry.previewId;
}

/**
 * Drop every remembered preview of a stage. A create claims items, so any cached preview whose
 * rule overlaps the new case (not only the one it was created from) carries pre-claim overlap
 * counts; re-previewing is what shows the new owner.
 */
export function forgetStagePreviews(stage: Stage) {
  const prefix = `${stage}|`;
  Array.from(recentPreviews.keys())
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => recentPreviews.delete(key));
}

/** @internal Test hook: drop every remembered preview. */
export function clearRecentPreviews() {
  recentPreviews.clear();
}

/**
 * Starts a preview for a rule and polls it until COMPLETED/FAILED. Re-running with a new rule
 * cancels interest in the previous preview. A rule previewed in the last few minutes (in this
 * tab) reuses that preview rather than starting a new query.
 */
export function useInvestigationPreview(stage: Stage) {
  const api = InvestigationsApiFactory();
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [startError, setStartError] = useState<InvestigationApiError | null>(
    null
  );
  const [starting, setStarting] = useState(false);
  const latestRule = useRef<string>("");
  const latestRuleValue = useRef<InvestigationRule>([]);

  const query = usePreviewQuery(stage, previewId);

  const run = async (
    rule: InvestigationRule,
    options?: { fresh?: boolean }
  ) => {
    const signature = JSON.stringify(rule);
    latestRule.current = signature;
    latestRuleValue.current = rule;
    setStartError(null);
    if (options?.fresh) forgetPreview(stage, rule);
    const reused = options?.fresh ? null : recentPreviewId(stage, rule);
    if (reused) {
      setPreviewId(reused);
      return;
    }
    setStarting(true);
    try {
      const data = await apiCall(() =>
        api.startInvestigationPreview(stage, { rule })
      );
      rememberPreview(stage, rule, data.previewId);
      if (latestRule.current === signature) setPreviewId(data.previewId);
    } catch (err) {
      setStartError(err as InvestigationApiError);
    } finally {
      setStarting(false);
    }
  };
  const reset = () => {
    setPreviewId(null);
    setStartError(null);
  };
  const preview = previewId ? query.data : undefined;
  // A failed preview must not be handed to the next caller of the same rule.
  useEffect(() => {
    if (preview?.status === "FAILED")
      forgetPreview(stage, latestRuleValue.current);
  }, [preview?.status, stage]);
  const pending =
    starting ||
    (!!previewId &&
      (!preview ||
        preview.status === "RUNNING" ||
        preview.status === "PROCESSING"));
  return {
    run,
    reset,
    preview,
    pending,
    error: startError || query.error,
  };
}

function useInvalidate(stage: Stage) {
  const queryClient = useQueryClient();
  return (investigationId?: string) => {
    queryClient
      .invalidateQueries(["investigations", "list", stage])
      .catch(console.error);
    queryClient
      .invalidateQueries(["investigations", "row", stage])
      .catch(console.error);
    // Ownership of TrackStars suggestions is derived from the active cases.
    queryClient
      .invalidateQueries(investigationKeys.suggestions(stage))
      .catch(console.error);
    if (investigationId) {
      queryClient
        .invalidateQueries(investigationKeys.detail(stage, investigationId))
        .catch(console.error);
      queryClient
        .invalidateQueries(investigationKeys.redrives(stage, investigationId))
        .catch(console.error);
    }
  };
}

export function useCreateInvestigation(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationCreateResult,
    InvestigationApiError,
    InvestigationCreateRequest
  >(async (request) => apiCall(() => api.createInvestigation(stage, request)), {
    onSuccess: (result, request) => {
      forgetPreviewById(request.previewId);
      // Other remembered previews may overlap the new case: their counts predate its claim.
      forgetStagePreviews(stage);
      invalidate(result.investigation?.id);
      // A duplicate closes on the spot and links itself on the owner's page.
      if (result.duplicateOfInvestigationId)
        invalidate(result.duplicateOfInvestigationId);
    },
    onError: (error, request) => {
      // The preview cannot be reused: either the snapshot moved on or an active case already has
      // this rule, and re-previewing is what shows the caller that case.
      if (error.code === "PREVIEW_STALE" || error.code === "DUPLICATE_RULE")
        forgetPreviewById(request.previewId);
      invalidate();
    },
  });
}

interface Versioned {
  investigationId: string;
}

export function useUpdateInvestigation(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    Investigation,
    InvestigationApiError,
    Versioned & { request: InvestigationUpdateRequest }
  >(
    async ({ investigationId, request }) =>
      apiCall(() => api.updateInvestigation(stage, investigationId, request)),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/** How often the bell re-checks the inbox (there is no server push). */
const INBOX_POLL_INTERVAL_MS = 60000;

/**
 * The signed-in user's notification inbox across all stages: the feed behind the bell. Polls every
 * minute and on window focus; fails closed (empty, zero unread) so the bell never breaks the page.
 */
export function useInvestigationInbox(enabled = true) {
  const api = InvestigationsApiFactory();
  return useQuery<InvestigationInbox, InvestigationApiError>(
    investigationKeys.inbox(),
    async () => apiCall(() => api.listInvestigationInbox(undefined, 50)),
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: true,
      refetchInterval: INBOX_POLL_INTERVAL_MS,
    }
  );
}

/** Mark inbox entries read (by id, by investigation, or all) and refresh the bell. */
export function useMarkInboxRead() {
  const api = InvestigationsApiFactory();
  const queryClient = useQueryClient();
  return useMutation<
    InvestigationInboxReadResult,
    InvestigationApiError,
    InvestigationInboxReadRequest
  >(async (request) => apiCall(() => api.markInvestigationInboxRead(request)), {
    onSettled: () => {
      queryClient
        .invalidateQueries(investigationKeys.inbox())
        .catch(console.error);
    },
  });
}

/**
 * Clear the owner's private notifications (the "N new" badge). Owner-only on the server; a
 * passive read, so it does not touch lastHumanActivityAt or updatedAt.
 */
export function useAcknowledgeNotifications(stage: Stage) {
  const update = useUpdateInvestigation(stage);
  const queryClient = useQueryClient();
  return {
    acknowledge: (investigation: Pick<Investigation, "id" | "version">) =>
      update
        .mutateAsync({
          investigationId: investigation.id,
          request: {
            expectedVersion: investigation.version,
            acknowledgeNotifications: true,
          },
        })
        .finally(() => {
          // The server also clears this case's rows in the cross-stage inbox behind the bell.
          queryClient
            .invalidateQueries(investigationKeys.inbox())
            .catch(console.error);
        }),
    isLoading: update.isLoading,
  };
}

export function useAddComment(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    Investigation,
    InvestigationApiError,
    Versioned & { request: InvestigationCommentRequest }
  >(
    async ({ investigationId, request }) =>
      apiCall(() =>
        api.addInvestigationComment(stage, investigationId, request)
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/**
 * Edit a comment's text and/or attachments (author or owner). The server keeps the previous text
 * (and attachment list) in `edits`; omitting `attachments` keeps the current ones.
 */
export function useEditComment(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationEvent,
    InvestigationApiError,
    {
      investigationId: string;
      eventId: string;
      text: string;
      attachments?: { key: string }[];
    }
  >(
    async ({ investigationId, eventId, text, attachments }) =>
      apiCall(() =>
        api.editInvestigationComment(stage, investigationId, eventId, {
          text,
          attachments,
        })
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/** Delete a comment (author or owner). The event stays in the audit log marked deleted. */
export function useDeleteComment(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationEvent,
    InvestigationApiError,
    { investigationId: string; eventId: string }
  >(
    async ({ investigationId, eventId }) =>
      apiCall(() =>
        api.deleteInvestigationComment(stage, investigationId, eventId)
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

export function useClaimInvestigation(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    Investigation,
    InvestigationApiError,
    Versioned & { expectedVersion: number }
  >(
    async ({ investigationId, expectedVersion }) =>
      apiCall(() =>
        api.claimInvestigation(stage, investigationId, {
          expectedVersion,
        })
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

export function useTransferInvestigation(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    Investigation,
    InvestigationApiError,
    Versioned & { request: InvestigationTransferRequest }
  >(
    async ({ investigationId, request }) =>
      apiCall(() => api.transferInvestigation(stage, investigationId, request)),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/**
 * Follow (`follow: true`) or stop following a case. Private bookkeeping on the server: no
 * expectedVersion, no timeline event, no activity timestamp.
 */
export function useFollowInvestigation(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    Investigation,
    InvestigationApiError,
    Versioned & { follow: boolean }
  >(
    async ({ investigationId, follow }) =>
      apiCall(() =>
        api.followInvestigation(stage, investigationId, { follow })
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/**
 * Private "remind me to check back" on a case: email + Slack to the caller at `remindAt`, or
 * `remindAt: null` to cancel. No expectedVersion, no timeline event; only the caller sees it
 * (`myReminder`).
 */
export function useSetInvestigationReminder(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    Investigation,
    InvestigationApiError,
    Versioned & { remindAt: string | null }
  >(
    async ({ investigationId, remindAt }) =>
      apiCall(() =>
        remindAt
          ? api.setInvestigationReminder(stage, investigationId, { remindAt })
          : api.cancelInvestigationReminder(stage, investigationId)
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/** Owner gives the case up without naming a successor; it goes back to "Needs an owner". */
export function useReleaseInvestigation(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    Investigation,
    InvestigationApiError,
    Versioned & { expectedVersion: number }
  >(
    async ({ investigationId, expectedVersion }) =>
      apiCall(() =>
        api.releaseInvestigation(stage, investigationId, {
          expectedVersion,
        })
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/**
 * Merge other active investigations into `investigationId` (the survivor). The survivor keeps its
 * id and owner; the sources close as RESOLVED/MERGED. Both the survivor's detail and every
 * source's detail are invalidated so a page open on either side refreshes.
 */
export function useMergeInvestigations(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  const queryClient = useQueryClient();
  return useMutation<
    InvestigationMergeResult,
    InvestigationApiError,
    Versioned & { request: InvestigationMergeRequest }
  >(
    async ({ investigationId, request }) =>
      apiCall(() => api.mergeInvestigations(stage, investigationId, request)),
    {
      onSettled: (_data, _error, variables) => {
        invalidate(variables.investigationId);
        variables.request.sourceInvestigationIds.forEach((id) => {
          queryClient
            .invalidateQueries(investigationKeys.detail(stage, id))
            .catch(console.error);
        });
      },
    }
  );
}

/** Per-case outcome of a bulk action; the UI reports both halves in one go. */
export interface BulkOutcome {
  succeeded: Investigation[];
  failed: Array<{ investigation: Investigation; error: InvestigationApiError }>;
}

export interface BulkProgress {
  done: number;
  total: number;
}

/** Requests in flight at once during a bulk action; keeps the Lambda concurrency flat. */
const BULK_CONCURRENCY = 3;

/**
 * Fan a per-case action out over a selection from the work-queue table. There is no bulk
 * endpoint on purpose: ownership is tracked per DLQ item, so a server-side bulk call would do
 * the same per-case writes, and the per-case endpoints keep their audit events, notifications
 * and `expectedVersion` checks. Each case uses the version the table loaded; a case someone
 * changed meanwhile fails with VERSION_CONFLICT and is reported rather than overwritten. The
 * list queries are invalidated once at the end instead of once per case.
 */
export function useBulkInvestigationActions(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  const [progress, setProgress] = useState<BulkProgress | null>(null);

  const run = async (
    targets: Investigation[],
    action: (investigation: Investigation) => Promise<unknown>
  ): Promise<BulkOutcome> => {
    const outcome: BulkOutcome = { succeeded: [], failed: [] };
    if (!targets.length) return outcome;
    setProgress({ done: 0, total: targets.length });
    let next = 0;
    const worker = async (): Promise<void> => {
      while (next < targets.length) {
        const investigation = targets[next++];
        try {
          // Sequential within each worker on purpose: BULK_CONCURRENCY workers share the queue.
          // eslint-disable-next-line no-await-in-loop
          await action(investigation);
          outcome.succeeded.push(investigation);
        } catch (err) {
          outcome.failed.push({ investigation, error: toApiError(err) });
        }
        setProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
      }
    };
    try {
      await Promise.all(
        Array.from(
          { length: Math.min(BULK_CONCURRENCY, targets.length) },
          worker
        )
      );
    } finally {
      setProgress(null);
      invalidate();
      outcome.succeeded.forEach((inv) => invalidate(inv.id));
    }
    // Keep the caller's order so the report reads like the selection did.
    const rank = new Map(targets.map((t, i) => [t.id, i]));
    outcome.succeeded.sort((a, b) => rank.get(a.id)! - rank.get(b.id)!);
    outcome.failed.sort(
      (a, b) => rank.get(a.investigation.id)! - rank.get(b.investigation.id)!
    );
    return outcome;
  };

  return {
    progress,
    isRunning: progress !== null,
    /** Hand every case to `newOwner` (anyone may reassign). */
    transfer: (targets: Investigation[], newOwner: string) =>
      run(targets, (inv) =>
        api.transferInvestigation(stage, inv.id, {
          expectedVersion: inv.version,
          newOwner,
        })
      ),
    /** Release every case back to "Needs an owner" (owner-only on the server). */
    release: (targets: Investigation[]) =>
      run(targets, (inv) =>
        api.releaseInvestigation(stage, inv.id, {
          expectedVersion: inv.version,
        })
      ),
    /** Apply the same status change to every case (owner-only on the server). */
    updateStatus: (
      targets: Investigation[],
      request: Omit<InvestigationUpdateRequest, "expectedVersion">
    ) =>
      run(targets, (inv) =>
        api.updateInvestigation(stage, inv.id, {
          ...request,
          expectedVersion: inv.version,
        })
      ),
    /**
     * Apply a per-case update (owner-only on the server). Used by bulk close, where each case's
     * existing conclusion must be merged rather than overwritten.
     */
    updateEach: (
      targets: Investigation[],
      build: (
        investigation: Investigation
      ) => Omit<InvestigationUpdateRequest, "expectedVersion">
    ) =>
      run(targets, (inv) =>
        api.updateInvestigation(stage, inv.id, {
          ...build(inv),
          expectedVersion: inv.version,
        })
      ),
  };
}

export function useStartRedrive(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    unknown,
    InvestigationApiError,
    Versioned & { expectedVersion: number; dryRun: boolean }
  >(
    async ({ investigationId, expectedVersion, dryRun }) =>
      apiCall(() =>
        api.startInvestigationRedrive(stage, investigationId, {
          expectedVersion,
          dryRun,
        })
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/**
 * Re-process the investigation's transactions through the order (document) redrive. Leaves the DLQ
 * and the case status alone; the attempt shows up in the redrive history as kind DOCUMENT_REDRIVE.
 */
export function useStartDocumentRedrive(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationRedriveAttempt,
    InvestigationApiError,
    Versioned & { expectedVersion: number }
  >(
    async ({ investigationId, expectedVersion }) =>
      apiCall(() =>
        api.startInvestigationDocumentRedrive(stage, investigationId, {
          expectedVersion,
        })
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/** File to upload in Lusca Search Prod for a redrive of this investigation (redriveMode=external). */
export function useExportExternalRedrive(stage: Stage) {
  const api = InvestigationsApiFactory();
  return useMutation<
    InvestigationExternalRedriveExport,
    InvestigationApiError,
    { investigationId: string; kind: InvestigationAttemptKind }
  >(async ({ investigationId, kind }) =>
    apiCall(() =>
      api.exportInvestigationExternalRedrive(stage, investigationId, { kind })
    )
  );
}

/** Link a redrive run in Lusca Search Prod back to the investigation (owner only). */
export function useLinkExternalRedrive(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationRedriveAttempt,
    InvestigationApiError,
    Versioned & {
      expectedVersion: number;
      kind: InvestigationAttemptKind;
      executionArn: string;
    }
  >(
    async ({ investigationId, expectedVersion, kind, executionArn }) =>
      apiCall(() =>
        api.linkInvestigationExternalRedrive(stage, investigationId, {
          expectedVersion,
          kind,
          executionArn,
        })
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/** Owner confirms a completed order redrive's CBEs were republished; unlocks the DLQ redrive. */
export function useConfirmDocumentRedrive(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    Investigation,
    InvestigationApiError,
    Versioned & { expectedVersion: number; attemptId: string }
  >(
    async ({ investigationId, expectedVersion, attemptId }) =>
      apiCall(() =>
        api.confirmInvestigationDocumentRedrive(
          stage,
          investigationId,
          attemptId,
          { expectedVersion }
        )
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/** Take back a mistaken CBE confirmation: back to the CBE check, DLQ redrive locked again. */
export function useUnconfirmDocumentRedrive(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    Investigation,
    InvestigationApiError,
    Versioned & { expectedVersion: number; attemptId: string }
  >(
    async ({ investigationId, expectedVersion, attemptId }) =>
      apiCall(() =>
        api.unconfirmInvestigationDocumentRedrive(
          stage,
          investigationId,
          attemptId,
          { expectedVersion }
        )
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/** "Remind me" on the CBE check: schedules an email + Slack reminder to the owner at `remindAt`. */
export function useSetDocumentRedriveReminder(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    Investigation,
    InvestigationApiError,
    Versioned & { expectedVersion: number; attemptId: string; remindAt: string }
  >(
    async ({ investigationId, expectedVersion, attemptId, remindAt }) =>
      apiCall(() =>
        api.setInvestigationDocumentRedriveReminder(
          stage,
          investigationId,
          attemptId,
          { expectedVersion, remindAt }
        )
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/**
 * Request an MCM-gated delete-without-redrive of the frozen manifest. Creates the Draft MCM and locks
 * the population (`pendingDeletion`) until the request is executed or cancelled.
 */
export function useRequestDeletion(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationDeletionResponse,
    InvestigationApiError,
    Versioned & {
      expectedVersion: number;
      justification: string;
      simLink?: string;
    }
  >(
    async ({ investigationId, expectedVersion, justification, simLink }) =>
      apiCall(() =>
        api.requestInvestigationDeletion(stage, investigationId, {
          expectedVersion,
          justification,
          ...(simLink ? { simLink } : {}),
        })
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/** Start the delete run for a pending request once its MCM is In Progress. */
export function useExecuteDeletion(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationDeletionResponse,
    InvestigationApiError,
    Versioned & { expectedVersion: number; attemptId: string }
  >(
    async ({ investigationId, expectedVersion, attemptId }) =>
      apiCall(() =>
        api.executeInvestigationDeletion(stage, investigationId, attemptId, {
          expectedVersion,
        })
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/** Withdraw a pending deletion request on the Lusca side (the MCM must be discarded in MCM). */
export function useCancelDeletion(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationDeletionResponse,
    InvestigationApiError,
    Versioned & { expectedVersion: number; attemptId: string }
  >(
    async ({ investigationId, expectedVersion, attemptId }) =>
      apiCall(() =>
        api.cancelInvestigationDeletion(stage, investigationId, attemptId, {
          expectedVersion,
        })
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

export function useRefreshPopulation(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationPreview,
    InvestigationApiError,
    Versioned & { expectedVersion: number }
  >(
    async ({ investigationId, expectedVersion }) =>
      apiCall(() =>
        api.refreshInvestigationPopulation(stage, investigationId, {
          expectedVersion,
        })
      ),
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/**
 * Adds a completed preview's unowned items to an existing case the caller owns (owner only; the
 * server refuses stale previews and more than 2,000 new items). The case's current version is
 * read first so the builder does not need to hold it.
 */
/** Owner widens the case's rule with another branch; matching items join at the next reconcile. */
export function useWidenRule(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationWidenRuleResponse,
    InvestigationApiError,
    Versioned & {
      rule: InvestigationPredicate[];
      patternId?: string;
      title?: string;
    }
  >(
    async ({ investigationId, rule, patternId, title }) => {
      const detail = await apiCall(() =>
        api.getInvestigation(stage, investigationId)
      );
      return apiCall(() =>
        api.widenInvestigationRule(stage, investigationId, {
          expectedVersion: detail.investigation?.version ?? -1,
          rule,
          ...(patternId ? { patternId } : {}),
          ...(title ? { title } : {}),
        })
      );
    },
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/** Owner releases the returned messages that came back with a new error (see the Redrives tab). */
export function useReleaseReturnedItems(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  const queryClient = useQueryClient();
  return useMutation<
    InvestigationReleaseReturnedResponse,
    InvestigationApiError,
    Versioned & { expectedVersion: number }
  >(
    ({ investigationId, expectedVersion }) =>
      apiCall(() =>
        api.releaseInvestigationReturnedItems(stage, investigationId, {
          expectedVersion,
        })
      ),
    {
      onSettled: (_data, _error, variables) => {
        invalidate(variables.investigationId);
        queryClient
          .invalidateQueries(
            investigationKeys.returnedItems(stage, variables.investigationId)
          )
          .catch(console.error);
      },
    }
  );
}

export function useExtendPopulation(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationExtendResponse,
    InvestigationApiError,
    Versioned & { previewId: string; widenRule?: boolean }
  >(
    async ({ investigationId, previewId, widenRule }) => {
      const detail = await apiCall(() =>
        api.getInvestigation(stage, investigationId)
      );
      return apiCall(() =>
        api.extendInvestigationPopulation(stage, investigationId, {
          expectedVersion: detail.investigation?.version ?? -1,
          previewId,
          ...(widenRule ? { widenRule } : {}),
        })
      );
    },
    {
      onSettled: (_data, _error, variables) =>
        invalidate(variables.investigationId),
    }
  );
}

/** Split by list (R22): dry-run counts, or create the child from the listed items. */
export function useSplitByList(stage: Stage) {
  const api = InvestigationsApiFactory();
  const invalidate = useInvalidate(stage);
  return useMutation<
    InvestigationSplitListResult,
    InvestigationApiError,
    Versioned & {
      lines: string[];
      dryRun?: boolean;
      title?: string;
      initialComment?: string;
    }
  >(
    async ({ investigationId, lines, dryRun, title, initialComment }) => {
      const detail = await apiCall(() =>
        api.getInvestigation(stage, investigationId)
      );
      return apiCall(() =>
        api.splitInvestigationByList(stage, investigationId, {
          expectedVersion: detail.investigation?.version ?? -1,
          lines,
          ...(dryRun ? { dryRun } : {}),
          ...(title ? { title } : {}),
          ...(initialComment ? { initialComment } : {}),
        })
      );
    },
    {
      onSettled: (_data, _error, variables) => {
        if (!variables.dryRun) invalidate(variables.investigationId);
      },
    }
  );
}

/**
 * Follows a population refresh to completion. The preview to watch comes from the
 * investigation's `pendingRefresh` marker (so a reload keeps showing progress) or from the
 * 202/409 the owner just received. Polls until the preview settles, then refetches the
 * investigation so the refrozen population and the cleared marker show up.
 */
export function usePopulationRefreshProgress(
  stage: Stage,
  investigation: Investigation,
  startedPreviewId: string | null
) {
  const invalidate = useInvalidate(stage);
  const previewId =
    investigation.pendingRefresh?.previewId ?? startedPreviewId ?? null;
  const query = usePreviewQuery(stage, previewId);
  // A disabled TanStack v4 query reports isLoading=true forever — gate on previewId.
  const preview = previewId ? query.data : undefined;
  // The worker refreezes right after the preview settles; refetch the case now and once more
  // shortly after so the new frozen population and cleared marker land. Once per settled
  // preview, in this hook only (an onSuccess on the shared preview key fired per observer).
  const settledPreviewId =
    preview && isSettledPreview(preview) ? preview.previewId : null;
  const hasPendingRefresh = !!investigation.pendingRefresh;
  const refreshedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!settledPreviewId || !hasPendingRefresh) return;
    if (refreshedFor.current === settledPreviewId) return;
    refreshedFor.current = settledPreviewId;
    invalidate(investigation.id);
    window.setTimeout(() => invalidate(investigation.id), 2500);
    // `invalidate` is a fresh closure every render; the effect is keyed on the settle itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledPreviewId, hasPendingRefresh, investigation.id]);
  const inProgress =
    !!previewId && !query.error && (!preview || !isSettledPreview(preview));
  const isOwnSettled =
    !!startedPreviewId &&
    preview?.previewId === startedPreviewId &&
    isSettledPreview(preview);
  return {
    inProgress,
    preview,
    startedAt: investigation.pendingRefresh?.startedAt ?? preview?.createdAt,
    startedBy: investigation.pendingRefresh?.startedBy,
    /** Set once the preview the owner started (this session) has settled. */
    settled: isOwnSettled ? preview : undefined,
  };
}

export async function fetchAgentPrompt(
  stage: Stage,
  investigationId: string
): Promise<string> {
  const api = InvestigationsApiFactory();
  const data = await apiCall(() =>
    api.getInvestigationAgentPrompt(stage, investigationId)
  );
  return data.prompt || "";
}

/**
 * Every returned item's DLQ key, read outside the table's paged query (so the table keeps showing
 * its current page) in the largest pages the API allows. Cursor-chained, so pages are sequential.
 */
export async function fetchAllReturnedPks(
  stage: Stage,
  investigationId: string
): Promise<string[]> {
  const api = InvestigationsApiFactory();
  const pks = new Set<string>();
  const readFrom = async (token?: string): Promise<void> => {
    const page = await apiCall(() =>
      api.listInvestigationReturnedItems(stage, investigationId, 200, token)
    );
    (page.items || []).forEach((i) => i.dlqPk && pks.add(i.dlqPk));
    if (page.nextToken) await readFrom(page.nextToken);
  };
  await readFrom();
  return [...pks];
}

export type ActivityScope = "all" | "mine";

const ACTIVITY_PAGE_SIZE = 50;

/**
 * The stage-wide activity feed: what people did on investigations, newest first (system and
 * worker events are not in it). `mine` keeps the caller's cases. Pages load on demand; page 1
 * refreshes every 30s and on window focus.
 */
export function useInvestigationActivity(stage: Stage, scope: ActivityScope) {
  const api = InvestigationsApiFactory();
  const query = useInfiniteQuery<
    InvestigationActivityPage,
    InvestigationApiError
  >(
    ["investigations", "activity", stage, scope],
    ({ pageParam }) =>
      apiCall(() =>
        api.listInvestigationActivity(
          stage,
          scope,
          ACTIVITY_PAGE_SIZE,
          pageParam as string | undefined
        )
      ),
    {
      retry: false,
      refetchOnWindowFocus: true,
      refetchInterval: 30000,
      getNextPageParam: (last) => last.nextToken || undefined,
    }
  );
  const pages = query.data?.pages ?? [];
  const seen = new Set<string>();
  const events = pages
    .flatMap((p) => p.events || [])
    .filter((e) => !seen.has(e.eventId || "") && !!seen.add(e.eventId || ""));
  const investigations: Record<string, InvestigationActivityCase> = {};
  pages.forEach((p) => Object.assign(investigations, p.investigations || {}));
  return { ...query, events, investigations };
}
